import {
  useState,
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  ReactNode,
} from 'react';
import { LegacySequelizeUser } from '@harvestiq/iiq-common';
import _ from 'lodash';
import { toPascalCase } from 'common';
import Loading from '../../components/Loading/Loading';
import { useRedirect } from '../Redirect';
import { useReferrerCookie } from '../../hooks/useReferrerCookie';
import { useAppSettings } from '../AppSettingsProvider';
import { Role } from '@harvestiq/constants';
import { useInsureIQApiClient } from '../InsureIQApiContextProvider';
import { useAuth0 } from '@auth0/auth0-react';
import { useHiqLogout } from '@harvestiq/ui/contexts/HiqLogoutProvider';
import { insureiqPages } from '@harvestiq/constants';

type IsRole = {
  isAdmin: boolean;
  isAgent: boolean;
  isSuperAdmin: boolean;
  isCustomer: boolean;
  isPartner: boolean;
};

type UserDefaults = {
  roles: string[];
} & IsRole;

export type UserFromContext = Omit<LegacySequelizeUser, 'roles'> & UserDefaults;

const userDefaults = (): UserDefaults => ({
  roles: [],
  isAdmin: false,
  isAgent: false,
  isSuperAdmin: false,
  isCustomer: false,
  isPartner: false,
});

// TODO: This method could be removed if we can get the pages to use the roles object array instead of a transformed roles string array
export const parseUserData = (
  userData: LegacySequelizeUser,
  allRoles: string[]
): UserFromContext => {
  const { roles: userRoles = [] } = userData;

  const parsedRoles = userRoles.reduce((acc, { id }) => {
    acc.roles.push(id);
    acc[`is${toPascalCase(id)}` as keyof IsRole] = true;
    return acc;
  }, userDefaults());

  // If the user is an admin, give them all roles
  if (parsedRoles.isAdmin && allRoles?.length) {
    allRoles.forEach((roleId) => {
      parsedRoles[`is${toPascalCase(roleId)}` as keyof IsRole] = true;
    });
  }

  return { ...userData, ...parsedRoles };
};

const isUserProfileComplete = (user: UserFromContext | null) => {
  return !!(user?.firstName && user?.lastName && user?.mobilePhoneNumber);
};

export type UserContextProps = {
  user: UserFromContext | null;
  isUserProfileComplete: boolean;
  setUser: (userData: LegacySequelizeUser | null) => void;
  getMe: () => Promise<LegacySequelizeUser | null>;
  hasRole: (roleId: Role | 'superAdmin') => boolean;
  hasRoles: (roles: readonly Role[]) => boolean;
};

export const UserContext = createContext<UserContextProps>(
  {} as UserContextProps
);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuth0();
  const { logoutFromAllHiqApps } = useHiqLogout();
  const { insureIQApiClient } = useInsureIQApiClient();
  const { deleteReferrerCookie } = useReferrerCookie();
  const { redirectOrNavigate } = useRedirect();
  const [user, setUserState] = useState<UserFromContext | null>(null);
  const { roles: allRoles = [] } = useAppSettings();

  const setUser = useCallback(
    (userData: LegacySequelizeUser | null) => {
      if (!userData) {
        setUserState(null);
        return;
      }
      const parsedUser = parseUserData(userData, allRoles);
      setUserState(parsedUser);

      // Referral cookie is only needed for unauthenticated users
      deleteReferrerCookie();
    },
    [deleteReferrerCookie, allRoles]
  );

  const getMe = useCallback(async () => {
    const userResp = await insureIQApiClient.getUserById({
      userId: user?.id ?? 'me',
    });
    if (userResp) {
      setUser(userResp);
      return userResp;
    }
    return null;
  }, [setUser]);

  useEffect(() => {
    const fetchUser = async () => {
      if (!isLoading && isAuthenticated && !user) {
        const userFromResponse = await insureIQApiClient.getUserById({
          userId: 'me',
        });

        if (userFromResponse) {
          setUser(userFromResponse);
          return redirectOrNavigate();
        }
        // We are authenticated with Auth0 but no IIQ user found. Log out and try auth again to create an IIQ user
        else {
          console.log(
            'No IIQ user data found with logged in Auth0 account, logging out'
          );
          const encodedMessage = encodeURIComponent(
            'There was an error signing you in. Please try again.'
          );
          await logoutFromAllHiqApps({
            logoutParams: {
              returnTo: `${window.location.origin}${insureiqPages.signIn.path}?msg=${encodedMessage}`,
            },
          });
        }
      }
    };

    void fetchUser();
  }, [isAuthenticated, isLoading, insureIQApiClient, redirectOrNavigate]);

  const hasRole = useCallback(
    (roleId: Role | 'superAdmin') => {
      if (!user) {
        return false;
      }

      const isSuperAdmin = user.roles.some((role) => role === 'superAdmin');

      if (isSuperAdmin) {
        return true; // super admin must have access to everything
      }

      if (roleId !== 'superAdmin' && user.isAdmin) {
        return true; // if user is an admin, they have access to everything except by super admin places
      }

      return user.roles.some((role) => role === roleId);
    },
    [user]
  );

  const hasRoles = useCallback(
    (roles: readonly Role[] = []) => {
      if (!user) {
        return false;
      }

      // empty roles requirement means user just needs to be logged in
      if (!roles.length) {
        return true;
      }

      return roles.some(hasRole);
    },
    [hasRole, user]
  );

  const isUserProfileCompleteVal = useMemo(
    () => isUserProfileComplete(user),
    [user]
  );

  const contextValue: UserContextProps = {
    user,
    isUserProfileComplete: isUserProfileCompleteVal,
    setUser,
    getMe,
    hasRole,
    hasRoles,
  };

  const canLoadApp = !isLoading && (!isAuthenticated || !!user);

  return (
    <UserContext.Provider value={contextValue}>
      {/* Ensure that children have access to user before rendering them */}
      {canLoadApp ? children : <Loading />}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
